import { defineStore, storeToRefs } from "pinia";
import { ref, watch } from "vue";

import { useProcessManager } from "@/composables/process-manager";
import { useStekManager } from "@/composables/stek-manager";
import { api, type types } from "@/lib/openapi";
import { onEntityChange } from "@/stores/cache-invalidation";
import { useReleaseStore } from "@/stores/release-store";

export type Lane = types.Lane;

export const useLanesStore = defineStore("lanes", () => {
    const isFetching = ref(false);
    const { process } = useProcessManager();
    const { stek } = useStekManager();
    const lanes = ref<Lane[]>();
    const releaseStore = useReleaseStore();

    const controller = ref(new AbortController());

    // Reload when a process definition lands on the invalidation bus — this
    // tab's own save, or the thread agent's set_process_definition /
    // generate_bpmn_draft on the branch. The backend re-derives the lanes from
    // the diagram, so the definition is the one attribute that changes them;
    // staying narrowed (rather than keying on the whole process, as
    // cache-invalidation.ts prescribes for lists) avoids a refetch on every
    // keystroke burst of a process description, which cannot move a lane.
    onEntityChange(
        (change) =>
            change.entityType === "process" &&
            change.attribute === "process_definition",
        () => {
            if (stek.value) {
                loadLanesForProcess();
            }
        }
    );

    async function loadLanesForProcess() {
        lanes.value = undefined;
        if (!process.value) {
            return;
        }
        controller.value.abort();
        controller.value = new AbortController();

        isFetching.value = true;
        const { response, data } =
            await api.startProcessLaneMgtGetLanesByProcessStartPost({
                body: {
                    process_id: process.value.identity,
                },
                signal: controller.value.signal,
            });

        if (!response.ok) {
            if (response.status === 499) {
                // Request was aborted
                return;
            }
            throw new Error("Failed to load lanes");
        }

        lanes.value = data;
        isFetching.value = false;
    }

    watch(
        [() => process.value?.identity, () => releaseStore.activeRevision],
        () => {
            loadLanesForProcess();
        },
        { immediate: true }
    );

    return { lanes, isFetching };
});

export const useLanesManager = () => storeToRefs(useLanesStore());
